import { css, cx } from "@emotion/css";
import { Card } from "@snapchat/snap-design-system";
import { Button, ButtonTypes } from "@snapchat/snap-design-system";
import { type FC, memo, type MouseEventHandler, type ReactNode, useState, useEffect } from "react";
import { useContentfulApi, type TermsContent } from "../hooks/useContentfulApi";
import { Visible } from "../../common/components";
import { FullPageError } from "../../common/components";
import {
    fadeEffectCss,
    eulaBodyCss,
    eulaBodyContainerCss,
    eulaBodyHeightWithButtons,
    eulaBodyHeightWithoutButtons,
    eulaButtonsContainerCss,
    absolutePositionCss,
} from "../styles";
import { EulaContent } from "./EulaContent";

export interface EulaAgreementProps {
    showButtons: boolean;
    onAgreeClick?: MouseEventHandler<HTMLButtonElement>;
    onCancelClick?: MouseEventHandler<HTMLButtonElement>;
    overrideContent?: ReactNode; // Used only for testing
}

export const EulaAgreement: FC<EulaAgreementProps> = memo(function EulaAgreement({
    showButtons,
    onAgreeClick,
    overrideContent,
}) {
    const { queryDeveloperTerms } = useContentfulApi();
    const [terms, setTerms] = useState<TermsContent | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<unknown>(null);

    useEffect(() => {
        let isMounted = true;
        async function loadTerms() {
            try {
                const loadedTerms = await queryDeveloperTerms();
                if (isMounted) {
                    setTerms(loadedTerms);
                }
            } catch (error) {
                console.info(error);
                if (isMounted) {
                    setError(error);
                }
            }
            if (isMounted) {
                setIsLoading(false);
            }
        }

        loadTerms();

        return () => {
            isMounted = false;
        };
    }, [queryDeveloperTerms]);

    if (error) {
        return <FullPageError />;
    }

    return (
        <Card
            titleBorder={false}
            className={css({
                // CSS inlined so the class can be removed when the component unmounts
                borderRadius: "18px",
                margin: "0 16px",
            })}
        >
            <div className={fadeEffectCss}>
                <div className={eulaBodyContainerCss}>
                    <div
                        className={cx(
                            eulaBodyCss,
                            showButtons ? eulaBodyHeightWithButtons : eulaBodyHeightWithoutButtons,
                        )}
                    >
                        <Visible when={Boolean(overrideContent)}>{overrideContent}</Visible>
                        <Visible when={!Boolean(overrideContent)}>
                            <EulaContent className={absolutePositionCss} terms={terms} isLoading={isLoading} />
                        </Visible>
                    </div>
                </div>
            </div>
            <Visible when={showButtons}>
                <div className={eulaButtonsContainerCss}>
                    <Button
                        data-testid="eula-agree"
                        isDisabled={isLoading}
                        onClick={onAgreeClick}
                        type={ButtonTypes.PRIMARY}
                    >
                        Accept &amp; Submit
                    </Button>
                </div>
            </Visible>
        </Card>
    );
});
